
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { TermsAndConditionsBlock } from "@/interfaces/terms-and-conditions.interface";
import moment from "moment-timezone";
import { getTermsContent } from "@/services/terms-and-conditions.service";

@Component
export default class TermsAndConditionsModal extends Vue {
  @Prop({ type: Boolean, required: true }) readonly value!: boolean;
  @Prop({ type: Object, required: true }) readonly terms!: TermsAndConditionsBlock;
  @Prop({ type: Boolean, default: false }) readonly loading!: boolean;

  contentLoading = false;
  termsHtml: string | null = null;
  ackChecked = false;
  scrolledToBottom = false;
  noScrollNeeded = false;
  didWarnContentLoad = false;
  iframeLoaded = false;
  iframeFailed = false;
  contentTried = false;
  iframeTimer: number | null = null;

  get isPersistent(): boolean {
    // Si no se puede saltear, no permitimos cerrar el modal (overlay/ESC)
    return this.terms.status.is_blocked || !this.terms.status.can_skip;
  }

  get termsUrl(): string {
    return this.terms?.active_terms?.url || "";
  }

  get formattedSkipUntil(): string {
    if (!this.terms.status.skip_until) return "";
    // Formatear fecha ISO a formato legible: solo fecha, sin hora
    return moment(this.terms.status.skip_until).format("DD/MM/YYYY");
  }

  get canAccept(): boolean {
    // Preferido (HTML proxy): habilitar por scroll al final o sin scroll necesario
    if (this.termsHtml) {
      return this.noScrollNeeded || this.scrolledToBottom;
    }
    // Fallback (iframe cross-domain): checkbox manual (solo si no hubo HTML)
    return this.ackChecked;
  }

  @Watch("value")
  async onOpenChange(open: boolean) {
    if (!open) return;
    await this.resetViewerState();
    this.startIframeFailoverTimer();
  }

  @Watch("termsUrl")
  async onTermsUrlChange() {
    if (!this.value) return;
    await this.resetViewerState();
    this.startIframeFailoverTimer();
  }

  resetViewerState() {
    this.contentLoading = false;
    this.termsHtml = null;
    this.ackChecked = false;
    this.scrolledToBottom = false;
    this.noScrollNeeded = false;
    this.didWarnContentLoad = false;
    this.iframeLoaded = false;
    this.iframeFailed = false;
    this.contentTried = false;
    if (this.iframeTimer) {
      clearTimeout(this.iframeTimer);
      this.iframeTimer = null;
    }
  }

  startIframeFailoverTimer() {
    // Si el iframe no dispara load en un tiempo razonable, lo tratamos como "falló"
    // y recién ahí intentamos /content.
    if (this.iframeTimer) clearTimeout(this.iframeTimer);
    this.iframeTimer = window.setTimeout(() => {
      if (!this.iframeLoaded) {
        this.iframeFailed = true;
        this.tryLoadTermsContent();
      }
    }, 6000);
  }

  onIframeLoad() {
    this.iframeLoaded = true;
    if (this.iframeTimer) {
      clearTimeout(this.iframeTimer);
      this.iframeTimer = null;
    }
    // Si el iframe cargó OK, NO llamamos /content (evitamos 401 “rojo”).
  }

  onIframeError() {
    this.iframeFailed = true;
    if (this.iframeTimer) {
      clearTimeout(this.iframeTimer);
      this.iframeTimer = null;
    }
    this.tryLoadTermsContent();
  }

  async tryLoadTermsContent() {
    if (this.contentTried) return;
    this.contentTried = true;
    await this.loadTermsContent();
  }

  async loadTermsContent() {
    if (!this.termsUrl) {
      this.termsHtml = null;
      return;
    }

    // Intentar proxy HTML solo cuando el iframe falló (error/timeout).
    this.contentLoading = true;
    try {
      const res = await getTermsContent(this.termsUrl);
      const html = (res?.data as any)?.html;
      if (typeof html === "string" && html.trim().length > 0) {
        this.termsHtml = html;
        await this.$nextTick();
        this.updateScrollState();
        return;
      }
      throw new Error("EMPTY_HTML");
    } catch (e) {
      this.termsHtml = null;
      // Si falla el proxy, caemos al iframe+checkbox (sin “ruido”).
      // 401/403: no molestar, solo dejar el link para abrir en nueva pestaña.
      const status = (e as any)?.response?.status;
      if (status !== 401 && status !== 403 && !this.didWarnContentLoad) {
        this.didWarnContentLoad = true;
        this.$toast?.error?.("No se pudo cargar el contenido. Abrilo en una pestaña nueva.");
      }
    } finally {
      this.contentLoading = false;
    }
  }

  onTermsScroll() {
    this.updateScrollState();
  }

  updateScrollState() {
    const el = this.$refs.termsScroll as HTMLElement | undefined;
    if (!el) return;

    const noScroll = el.scrollHeight <= el.clientHeight + 1;
    this.noScrollNeeded = noScroll;

    const threshold = 16;
    this.scrolledToBottom =
      el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
  }
}
